import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as taskService from "../../../../models/services/taskService";
import { SaveDataObj } from "source/framework/saveData";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { TaskStateListChip } from "../../TaskStateListChip";
import { ITaskStateListItem, TaskStateListItem } from "source/models/taskState/taskStateListItem";
import { ListStateIconSuccess } from "source/components/framework/List/ListStateIconSuccess";
import { TaskStateDialog } from "../../TaskSettingsDialog/TaskStateList/TaskStateDialog";
import { useEffect, useState } from "react";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { Box } from "@mui/material";
import { List } from "source/components/framework/List/List";
import { TaskStateEdit } from "source/models/taskState/taskStateEdit";
import { TaskStateEditItem } from "source/models/taskState/taskStateEditItem";

interface ITaskTypeDialogTaskStateListProp {
    companyId: string;
    taskTypeId: string;
    taskStates: ITaskStateListItem[];
    onChange: (taskStates: ITaskStateListItem[]) => void;
}

interface ITaskTypeDialogTaskStateListState {
    items: ITaskStateListItem[];
    isReadOnly: boolean;
    selectedId: string;
    isLoading: boolean;
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export const TaskTypeDialogTaskStateList = (props: ITaskTypeDialogTaskStateListProp) => {
    const [state, setState] = useState<ITaskTypeDialogTaskStateListState>({
        items: props.taskStates,
        isReadOnly: props.taskStates.length < 1,
        isLoading: false,
        selectedId: null,
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false,
    });

    const searchItems = async (moveNext: boolean = false, setEditDialogData: boolean = true) => {
        setState(old => ({
            ...old,
            isLoading: true
        }));
        const result = await AppUtils.callService(() => taskService.getTaskStateListItems(props.companyId, props.taskTypeId, 10000, 1, null, "activeState", false), false, false,
            () => setState(old => ({
                ...old,
                isLoading: false
            })));
        if (!result) return;
        const items = List.getListItems(state.items, state.selectedId, [], result.items, true, false);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        setState(old => ({
            ...old,
            items: items.items,
            isReadOnly: result.items.length < 1,
            selectedId,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        }));
        //To cache latest result to form some default values do not flash when switching between pages in task type
        props.onChange(items.items);
    };

    const refreshList = async (setEducationDialogData: boolean = true) => {
        await searchItems(false, setEducationDialogData);
    };

    const isNewTaskType = props.taskTypeId === Base.emptyGuid;

    useEffect(() => {
        if (!props.taskTypeId || isNewTaskType) return;
        searchItems();
    }, [props.taskTypeId]);

    useEffect(() => {
        if (!props.taskTypeId || !isNewTaskType) return;
        setState(old => ({
            ...old,
            items: props.taskStates,
            selectedId: props.taskStates.find(i => i.id === old.selectedId)?.id || null,
            isReadOnly: props.taskStates.length < 1,
        }));
    }, [props.taskStates]);

    const handleSearchLines = (_sortColumn: string, _sortOrderIsAsc: boolean, nextPage: boolean) => {
        if (isNewTaskType) return;
        searchItems(!nextPage, false);
    };

    const handleSetSelectedIds = (selectedId: string, _checkedIds: string[]) => {
        setState(old => ({
            ...old,
            selectedId,
        }));
    };

    const handleAdd = () => {
        setState(old => ({
            ...old,
            showEditDialog: true,
            editDialogId: Base.emptyGuid,
            editDialogHasPrevious: false,
            editDialogHasNext: false
        }));
    };

    const handleOpenLine = (id: string) => {
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        setState(old => ({
            ...old,
            showEditDialog: true,
            editDialogId: id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1,
            selectedId: id
        }));
    };

    const handleSaved = (item: ITaskStateListItem) => {
        if (isNewTaskType) {
            const taskStates = [...props.taskStates.filter(i => i.id !== item.id), item];
            props.onChange(TaskStateListItem.sortTaskStateListItems(taskStates));
        } else {
            refreshList(false);
        }
    };

    const handleEditDialogCancel = (doRefreshList: boolean) => {
        setState(old => ({
            ...old,
            showEditDialog: false
        }));
        if (!doRefreshList || isNewTaskType) return;
        refreshList(false);
    };

    const getRemoveSaveData = (props: ITaskTypeDialogTaskStateListProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("taskTypeId", props.taskTypeId);
        result.append("ids", ids);
        return result;
    };

    const remove = async (ids: string[]): Promise<ApiSuccess> => {
        if (ids.length < 1) return;
        if (!isNewTaskType) {
            const saveData = getRemoveSaveData(props, ids);
            const result = await AppUtils.callService(() => taskService.removeTaskStates(saveData.obj));
            if (!result) return null;
            refreshList();
        } else {
            props.onChange(props.taskStates.filter(i => ids.indexOf(i.id) < 0));
        }
        return new ApiSuccess();
    };

    const handleRemove = async () => {
        const selectedIds = List.getSelectedIds(state.selectedId, []);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedTaskStates
            : String.format(Translations.DoYouReallyWantToRemoveTaskStateParameter, selectedItems[0].code), false) === ConfirmationDialogResult.Yes) {
            remove(selectedIds);
        }
    };

    const setEditDialogItemByIndex = (index: number) => {
        if (index < 0) return;
        setState(old => ({
            ...old,
            selectedId: state.items[index].id,
            editDialogId: state.items[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < state.items.length - 1
        }));
    };

    return (
        <>
            <DialogSectionCard
                headerTitle={Translations.TaskStates}
                buttons={
                    <ListIconButtonsItem
                        addTooltip={Translations.AddTaskState}
                        removeTooltip={Translations.RemoveTaskState}
                        removeDisabled={!state.selectedId}
                        onAdd={handleAdd}
                        onRemove={!state.isReadOnly ? handleRemove : null}
                    />
                }
            >
                <Box sx={{ margin: "-1rem", height: "calc(100% + 40px)" }}>
                    <List
                        intialized={true}
                        isLoading={state.isLoading}
                        hasMore={false}
                        globalLoadingInUse={true}
                        noDataMessage={Translations.TaskDefaultStatesInUse}
                        height="100%"
                        lines={state.items.map((item) => {
                            return {
                                columns: [
                                    {
                                        size: 10,
                                        content: <TaskStateListChip
                                            code={item.code}
                                            color={item.color}
                                            inactive={item.activeState < 1}
                                            onClick={() => handleOpenLine(item.id)}
                                        />
                                    },
                                    { size: 2, content: item.activeState < 1 ? null : <ListStateIconSuccess tooltip={Translations.InUse} /> }
                                ],
                                id: item.id,
                                classes: item.activeState < 1 ? "disabled" : ""
                            };
                        })}
                        sortColumn={""}
                        sortOrderIsAsc={true}
                        selectedId={state.selectedId}
                        checkedIds={[]}
                        onSetSelectedIds={handleSetSelectedIds}
                        onSearchLines={handleSearchLines}
                        onOpenLine={handleOpenLine}
                    />
                </Box>
            </DialogSectionCard>
            {(state.showEditDialog && !!state.editDialogId) &&
                <TaskStateDialog
                    isReadOnly={state.isReadOnly && state.editDialogId !== Base.emptyGuid}
                    id={state.editDialogId}
                    companyId={props.companyId}
                    taskTypeId={props.taskTypeId}
                    hasPrevious={state.editDialogHasPrevious}
                    hasNext={state.editDialogHasNext}
                    onSaved={handleSaved}
                    onClose={handleEditDialogCancel}
                    onRemove={remove}
                    onRefreshList={async () => {
                        if (isNewTaskType) return;
                        await refreshList(false);
                    }}
                    onPrevious={() => List.moveToPrevious(state, setEditDialogItemByIndex)}
                    onNext={() => List.moveToNext(state, setEditDialogItemByIndex)}
                    onGetTaskStateEdit={isNewTaskType
                        ? (_companyId: string, id: string) => {
                            let item = new TaskStateListItem();
                            if (id === Base.emptyGuid) {
                                item.id = Base.emptyGuid;
                                item.activeState = 1;
                            } else {
                                item = props.taskStates.find(i => i.id === id);
                                if (!item) return Base.getPromiseResult(null);
                            }
                            const result = new TaskStateEdit();
                            result.item = new TaskStateEditItem(item);
                            return Base.getPromiseResult(result);
                        }
                        : undefined
                    }
                />
            }
        </>
    );
};